'use client';

import Container from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import { PostCard } from '@/components/ui/post-card';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import type { SectionBgVariant } from '@/lib/types/misc';
import { cn } from '@/lib/utils';
import type { Document } from '@contentful/rich-text-types';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import styles from './insights-section.module.scss';

export type InsightCardData = {
  image?: string | null;
  imageAlt?: string | null;
  tags?: string[];
  resourceType?: string | null;
  title?: string | null;
  description?: string | null;
  date?: string | null;
  minRead?: number | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
};

export type InsightsSectionProps = {
  sectionId?: string;
  badge?: string;
  title: string | Document;

  subHeading?: Document;
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  layoutMode?: 'carousel' | 'grid';
  cards: InsightCardData[];
  enableAutoscroll?: boolean;
  autoscrollSpeed?: number;
  className?: string;
};

// ── Grid layout ───────────────────────────────────────────────────────────────

const GridLayout = ({ cards }: { cards: InsightCardData[] }) => (
  <div className={cn(styles.cards_grid, cards.length === 3 && styles['cards_grid--three'])}>
    {cards.slice(0, 4).map((card, i) => (
      <PostCard key={`${card.title}-${i}`} {...card} />
    ))}
  </div>
);

// ── Section ───────────────────────────────────────────────────────────────────

export const InsightsSection = ({
  sectionId,
  badge,
  title,

  subHeading,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
  showDecorativeLines = false,
  showDecorativeDots = false,
  layoutMode = 'carousel',
  cards,
  enableAutoscroll,
  autoscrollSpeed = 4000,
  className,
}: InsightsSectionProps) => {
  const Tag = 'h2' as const;
  const isDark = isBackgroundDark;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickStartRef = useRef<number>(0);
  const pauseElapsedRef = useRef<number>(0);

  // Derived — no window check so no hydration mismatch
  const shouldAutoscroll = layoutMode === 'carousel' && !!enableAutoscroll;

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    layoutMode === 'carousel'
      ? {
          initial: 0,
          loop: true,
          slides: {
            perView: 1.15,
            spacing: 20,
          },
          breakpoints: {
            '(min-width: 768px)': {
              slides: { perView: 2.15, spacing: 20 },
            },
            '(min-width: 1024px)': {
              slides: { perView: 3.15, spacing: 20 },
            },
            '(min-width: 1280px)': {
              slides: { perView: 3.5, spacing: 20 },
            },
          },
          slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
            setProgressKey((k) => k + 1);
          },
        }
      : false,
  );

  const scheduleNext = (delay?: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    tickStartRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      instanceRef.current?.next();
      scheduleNext();
    }, delay ?? autoscrollSpeed);
  };

  const restartTimer = () => {
    if (!shouldAutoscroll) return;
    scheduleNext();
  };

  useEffect(() => {
    if (!shouldAutoscroll) return;
    setProgressActive(true);
    scheduleNext();
    return () => {
      setProgressActive(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoscroll, autoscrollSpeed, instanceRef]);

  const handleMouseEnter = () => {
    if (!shouldAutoscroll) return;
    pauseElapsedRef.current = Date.now() - tickStartRef.current;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (!shouldAutoscroll) return;
    setIsHovered(false);
    const remaining = Math.max(0, autoscrollSpeed - pauseElapsedRef.current);
    scheduleNext(remaining);
  };

  const goToSlide = (index: number) => {
    instanceRef.current?.moveToIdx(index);
    restartTimer();
  };

  const goPrev = () => {
    const slider = instanceRef.current;
    if (!slider) return;
    slider.moveToIdx(slider.track.details.abs - 1);
    restartTimer();
  };

  const goNext = () => {
    const slider = instanceRef.current;
    if (!slider) return;
    slider.moveToIdx(slider.track.details.abs + 1);
    restartTimer();
  };

  const backgroundStyle =
    backgroundVariant === 'purple-gradient'
      ? {
          background:
            'linear-gradient(90deg, #36136A 0%, #3C2877 29%, #444187 64%, #474C8E 81%, #4B5996 100%)',
        }
      : backgroundVariant === 'custom' && backgroundColorFrom
        ? { background: backgroundColorFrom }
        : undefined;

  return (
    <section
      id={sectionId}
      className={cn(
        styles.section,
        styles[`section--bg-${backgroundVariant}`],
        isDark && styles['section--dark'],
        className,
      )}
      style={backgroundStyle}
    >
      {(showDecorativeLines || showDecorativeDots) && (
        <DecorativeLines
          showDots={showDecorativeDots}
          showLines={showDecorativeLines}
          variant={isDark ? 'dark' : 'light'}
        />
      )}

      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.section__background}>
          <Image
            src={backgroundImage}
            alt="Insights section background"
            className={styles.section__background_image}
            fill
          />
        </div>
      )}

      <Container>
        <div className={styles.inner}>
          {/* ── Header ── */}
          <div className={styles.header}>
            <div className={styles.header__left}>
              {badge && <Badge text={badge} />}
              <div
                className={cn(
                  styles.header__heading,
                  isDark ? styles['header__heading--dark'] : styles['header__heading--light'],
                )}
              >
                {typeof title === 'string' ? (
                  <Tag>{title}</Tag>
                ) : (
                  <RichTextViewer content={title} dark={isDark} />
                )}
              </div>
              {subHeading && (
                <div
                  className={cn(
                    styles.header__subheading,
                    isDark
                      ? styles['header__subheading--dark']
                      : styles['header__subheading--light'],
                  )}
                >
                  <RichTextViewer content={subHeading} dark={isDark} />
                </div>
              )}
            </div>

            {/* Carousel nav — dots + next arrow, visible when carousel */}
            {layoutMode === 'carousel' && cards.length > 1 && (
              <div className={styles.header__nav}>
                <div className={styles.nav__dots}>
                  {cards.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToSlide(i)}
                      className={cn(
                        styles.nav__dot,
                        i === currentSlide && styles['nav__dot--active'],
                        i === currentSlide && progressActive && styles['nav__dot--autoscroll'],
                        isDark && styles['nav__dot--dark'],
                      )}
                      aria-label={`Go to slide ${i + 1}`}
                    >
                      {progressActive && i === currentSlide && (
                        <span
                          key={progressKey}
                          className={styles.nav__progress}
                          style={
                            {
                              '--progress-duration': `${autoscrollSpeed}ms`,
                              animationPlayState: isHovered ? 'paused' : 'running',
                            } as React.CSSProperties
                          }
                        />
                      )}
                    </button>
                  ))}
                </div>
                <div className={cn(styles.nav__arrows, 'hidden md:flex')}>
                  <button
                    onClick={goPrev}
                    className={styles.nav__arrow}
                    aria-label="Previous slide"
                  >
                    <ArrowRight width={24} height={24} className={styles.nav__arrow_prev} />
                  </button>
                  <button onClick={goNext} className={styles.nav__arrow} aria-label="Next slide">
                    <ArrowRight width={24} height={24} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Cards ── */}
          {layoutMode === 'grid' ? (
            <GridLayout cards={cards} />
          ) : (
            <div
              className={styles.carousel}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div
                ref={sliderRef}
                className={cn('keen-slider', styles.slider, styles.carousel__track)}
              >
                {cards.map((card, i) => (
                  <div
                    key={`${card.title}-${i}`}
                    className={cn('keen-slider__slide card-shadow', styles.slide)}
                  >
                    <PostCard {...card} className={styles.carousel__card} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};
